import { SALES_ORDER_PICK_READ_PERMISSIONS } from '@nudool/shared';

export type RouteAccess = Readonly<{
  label: string;
  required?: readonly string[];
  /** Holding any one of these opens the route in place of its own `required`. */
  admitsAnyOf?: readonly string[];
  /** Permissions the page's queries read beyond `required`; without them parts of the page come back blank. */
  lookups?: readonly string[];
  requiresBrandMembership?: boolean;
}>;

/** Keys are route templates; an entry also needs every ancestor entry's `required` and `lookups`. */
export const ROUTE_ACCESS = {
  '/company-setup': { label: 'Company Setup', required: ['company:editDetails'] },
  '/company-settings': {
    label: 'Company Settings',
    required: ['settings:view'],
    lookups: ['carrier:view', 'shipping:view'],
  },
  '/departments': { label: 'Departments', required: ['department:view'] },
  '/roles': { label: 'Roles & Permissions', required: ['userGroup:view'] },
  '/users': { label: 'Users', required: ['user:view'] },
  '/parts': { label: 'Parts', required: ['part:view'], lookups: ['uom:view'] },
  '/parts/new': { label: 'New Part', required: ['part:edit'] },
  '/parts/$partId': {
    label: 'Part',
    lookups: ['user:view', 'inventory:view', 'vendor:view', 'locationGroup:view', 'location:view'],
  },
  '/warehouses': { label: 'Warehouses', required: ['locationGroup:view'] },
  '/warehouses/new': { label: 'New Warehouse', required: ['locationGroup:edit', 'location:edit'] },
  '/warehouses/$locationGroupId/new': { label: 'New Location', required: ['location:edit'] },
  '/warehouses/$locationGroupId/$locationId': { label: 'Location', required: ['location:view'] },
  '/customers': {
    label: 'Customers',
    required: ['customer:view'],
    lookups: ['currency:view', 'paymentTerms:view', 'shipping:view', 'taxRate:view'],
  },
  '/customers/new': { label: 'New Customer', required: ['customer:edit'] },
  '/customers/$customerId': { label: 'Customer', lookups: ['brand:view'] },
  '/vendors': {
    label: 'Vendors',
    required: ['vendor:view'],
    lookups: ['currency:view', 'carrier:view', 'paymentTerms:view', 'shipping:view'],
  },
  '/vendors/new': { label: 'New Vendor', required: ['vendor:edit'] },
  '/brands': { label: 'Brands', required: ['brand:view'] },
  '/brands/new': { label: 'New Brand', required: ['brand:edit'] },
  '/products': {
    label: 'Products',
    required: ['product:view'],
    lookups: ['brand:view', 'uom:view'],
  },
  '/products/new': { label: 'New Product', required: ['product:edit'] },
  '/product-categories': {
    label: 'Product Categories',
    required: ['product:view'],
    lookups: ['brand:view'],
  },
  '/sales-orders': { label: 'Sales Orders', required: ['salesOrder:view'] },
  '/sales-orders/new': { label: 'New Sales Order', required: ['salesOrder:edit'] },
  '/sales-orders/$salesOrderId': {
    label: 'Sales Order',
    lookups: [
      'promotion:view',
      'carrier:view',
      'paymentTerms:view',
      'locationGroup:view',
      'shipping:view',
      'user:view',
      'account:view',
      'contact:view',
      'product:view',
      'uom:view',
    ],
  },
  '/accounts-receivable': {
    label: 'Accounts Receivable',
    required: ['accountsReceivable:view'],
  },
  '/invoices': { label: 'Invoices', required: ['invoice:view'] },
  '/invoices/$invoiceId': { label: 'Invoice' },
  '/credits': {
    label: 'Credits',
    required: ['promotion:view'],
    lookups: ['customer:view', 'brand:view', 'user:view', 'product:view'],
  },
  '/pricing': {
    label: 'Pricing',
    required: ['pricingEngine:view'],
    lookups: ['paymentTerms:view'],
  },
  '/pricing-rules': {
    label: 'Pricing Rules',
    required: ['pricingEngine:view'],
    lookups: ['brand:view', 'product:view', 'customer:view', 'paymentTerms:view'],
  },
  '/integrations/leaflink': {
    label: 'LeafLink',
    required: ['leafLinkIntegration:view', 'brand:view'],
  },
  '/integrations/leaflink/$leafLinkIntegrationId/list-products': {
    label: 'List Products',
    required: ['product:view'],
  },
  '/integrations/sage-intacct': {
    label: 'Sage Intacct',
    required: ['accountingIntegration:view'],
  },
  '/events': { label: 'Events', required: ['event:view'], requiresBrandMembership: true },
  '/events/new': { label: 'New Event', required: ['event:edit'] },
  '/events/templates': { label: 'Event Templates', required: ['event:template'] },
  '/events/tags': { label: 'Event Tags', required: ['event:template'] },
  '/events/contacts': { label: 'Event Contacts' },
  '/events/$eventId': { label: 'Event' },
  '/purchase-orders': {
    label: 'Purchase Orders',
    required: ['purchaseOrder:view'],
    lookups: ['vendor:view', 'user:view'],
  },
  '/purchase-orders/new': { label: 'New Purchase Order', required: ['purchaseOrder:edit'] },
  '/purchase-orders/$purchaseOrderId': {
    label: 'Purchase Order',
    lookups: ['locationGroup:view', 'brand:view', 'paymentTerms:view', 'carrier:view'],
  },
  '/supplier-returns': { label: 'Supplier Returns', required: ['rma:view'] },
  '/receiving': {
    label: 'Receiving',
    required: ['receiving:view'],
    lookups: ['purchaseOrder:view', 'vendor:view', 'locationGroup:view'],
  },
  '/receiving/$purchaseOrderId': {
    label: 'Receive',
    lookups: ['carrier:view', 'paymentTerms:view', 'user:view', 'part:view', 'uom:view'],
  },
  '/receiving/transfer/$transferOrderId': { label: 'Receive Transfer' },
  '/picking': {
    label: 'Picking',
    required: ['picking:view'],
    admitsAnyOf: SALES_ORDER_PICK_READ_PERMISSIONS,
  },
  '/picking/': { label: 'Picking', required: ['picking:view'] },
  '/inventory': { label: 'Inventory', required: ['inventory:view'], lookups: ['uom:view'] },
  '/transfer-orders': { label: 'Transfer Orders', required: ['transferOrder:view'] },
  '/transfer-orders/new': {
    label: 'New Transfer Order',
    required: [
      'transferOrder:edit',
      'brand:view',
      'part:view',
      'locationGroup:view',
      'carrier:view',
    ],
  },
  '/manufacturing': {
    label: 'Manufacturing',
    required: ['manufactureOrder:view'],
    lookups: ['brand:view', 'locationGroup:view', 'user:view'],
  },
  '/manufacturing/new': { label: 'New Manufacturing Order', required: ['manufactureOrder:edit'] },
  '/manufacturing/$manufacturingOrderId': {
    label: 'Manufacturing Order',
    lookups: ['part:view', 'uom:view', 'bom:view'],
  },
  '/manufacturing/work-orders': {
    label: 'Work Orders',
    lookups: ['workOrder:view', 'part:view', 'uom:view'],
  },
  '/boms': {
    label: 'Bill of Materials',
    required: ['bom:view'],
    lookups: ['brand:view', 'locationGroup:view', 'part:view'],
  },
  '/boms/new': { label: 'New BOM', required: ['bom:edit', 'locationGroup:view'] },
  '/boms/$bomId': { label: 'BOM', lookups: ['uom:view'] },
  '/audit-trail': { label: 'Audit Trail', required: ['auditLog:view'] },
  '/custom-reports': { label: 'Custom Reports', required: ['reports:view'] },
  '/design-system': { label: 'Design System', required: ['settings:view'] },
  '/api': { label: 'API', required: ['settings:view'] },
  '/workflows': { label: 'Workflows', required: ['settings:view'] },
} as const satisfies Record<string, RouteAccess>;

export type AccessRoute = keyof typeof ROUTE_ACCESS;
